import { useEffect, useRef, useState } from "react";
import styled, { keyframes, css } from "styled-components";

const T1 = 2000;
const T2 = 3150;
const T3 = 4900;

/* ---------- keyframe animations ---------- */
const dropIn = keyframes`
  0% { transform: translateY(-40px); opacity: 0; }
  70% { transform: translateY(6px); opacity: 1; }
  100% { transform: translateY(0); opacity: 1; }
`;

const fadeUp = keyframes`
  from { transform: translateY(18px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
`;

const popScale = keyframes`
  0% { transform: scale(0.4); opacity: 0; }
  70% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

/* ---------- styled components ---------- */

const Wrapper = styled.div`
  width: min(420px, 80vw);
  max-width: 100%;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Text",
    "Segoe UI",
    sans-serif;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 22px;
  border-radius: 999px;
  background: #ffffff;
  color: #111111;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.12);
  margin-bottom: 18px;
  white-space: nowrap;
`;

const FlowCard = styled.div`
  border-radius: 22px;
  padding: 2px;
  background: radial-gradient(circle at top left, #ffe2c8, #ffffff);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.12);
`;

const FlowInner = styled.div`
  border-radius: 20px;
  background: #fffdfc;
  padding: 24px 22px;
  min-height: 280px;
  position: relative;
  overflow: visible;
`;

const Scene = styled.div`
  position: absolute;
  inset: 24px 22px;
  border-radius: 18px;
  opacity: ${(props) => (props.active ? 1 : 0)};
  transform: translateY(${(props) => (props.active ? 0 : 14)}px);
  pointer-events: ${(props) => (props.active ? "auto" : "none")};
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
`;

/* Scene 1 */
const S1Header = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const S1Text = styled.div`
  font-size: 12px;
  color: #7b746c;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const FileBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8f4f1;
  border: 1px solid rgba(0, 0, 0, 0.04);
  margin-bottom: 18px;
  transform: translateY(-36px);
  opacity: 0;

  ${(props) =>
    props.animate &&
    css`
      animation: ${dropIn} 0.7s ease-out forwards;
      animation-delay: 0.3s;
    `}
`;

const FileIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: #eb5e28;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 18px;
  flex-shrink: 0;
`;

const FileMain = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #231f1a;
`;

const FileSub = styled.div`
  font-size: 11px;
  color: #7b746c;
`;

const FileCheck = styled.div`
  margin-left: auto;
  color: #2f9e44;
  font-size: 16px;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 0.9rem;
  transform: translateY(18px);
  opacity: 0;

  &:hover {
    cursor: default;
    transform: translate(0);
    box-shadow: none;
  }

  ${(props) =>
    props.animate &&
    css`
      animation: ${fadeUp} 0.5s ease-out forwards;
      animation-delay: 0.95s;
    `}
`;

/* Scene 2 */
const S2Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const S2Caption = styled.div`
  font-size: 12px;
  color: #7b746c;
  margin-bottom: 16px;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #eee4db;
  overflow: hidden;
  margin-bottom: 18px;
`;

const ProgressInner = styled.div`
  width: ${(props) => props.width}%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #ffc28e, #eb5e28);
  transition: width 2.4s linear;
`;

const Steps = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: #7b746c;
`;

const StepItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;

  .check {
    width: 17px;
    height: 17px;
    border-radius: 999px;
    border: 1px solid #c9bfb3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: #eb5e28;
    opacity: 0;
    transform: scale(0.5);
    transition:
      opacity 0.22s ease,
      transform 0.22s ease,
      background 0.22s ease,
      border-color 0.22s ease;
  }

  ${(props) =>
    props.done &&
    css`
      .check {
        opacity: 1;
        transform: scale(1);
        background: #ffe5d6;
        border-color: transparent;
      }
    `}
`;

/* Scene 3 */
const AssistantName = styled.div`
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const ChatBubble = styled.div`
  border-radius: 16px;
  padding: 11px 13px;
  background: #fef6ff;
  margin-bottom: 16px;
`;

const TypingText = styled.div`
  font-size: 12px;
  color: #231f1a;
  line-height: 1.45;
  min-height: 50px;
  white-space: pre-wrap;
`;

const InputArea = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fdf8f5;
  padding: 9px 10px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #a09a92;
`;

const Hint = styled.div`
  font-size: 10px;
  color: #7b746c;
`;

/* Scene 4 */
const SuccessWrapper = styled.div`
  text-align: center;
  padding-top: 12px;
`;

const SuccessIcon = styled.div`
  width: 88px;
  height: 88px;
  border-radius: 50%;
  margin: 0 auto 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 26px;
  background: radial-gradient(circle at 30% 0, #ffcfaa, #eb5e28);
  /* box-shadow:
    0 24px 40px rgba(235, 94, 40, 0.45),
    0 0 0 18px rgba(235, 94, 40, 0.06); */
  transform: scale(0.7);
  opacity: 0;

  ${(props) =>
    props.show &&
    css`
      animation: ${popScale} 0.4s ease-out forwards;
    `}
`;

const SuccessTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const SuccessText = styled.div`
  font-size: 12px;
  color: #7b746c;
  margin-bottom: 16px;
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 7px 18px;
  border-radius: 999px;
  border: 1px solid #eb5e28;
  background: #fff6f1;
  color: #eb5e28;
  font-size: 12px;
  font-weight: 600;
  text-shadow: none;

  &:hover {
    cursor: default;
    transform: translate(0);
    box-shadow: none;
  }
`;

/* Replay */
const ReplayButton = styled.button`
  margin-top: 10px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  padding: 6px 14px;
  font-size: 11px;
  color: #6b635a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? 0 : 6)}px);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
  pointer-events: ${(props) => (props.visible ? "auto" : "none")};
  text-shadow: none;
`;

/* ---------- React component ---------- */

function ApplicantFlow() {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [fileAnim, setFileAnim] = useState(false);
  const [btnAnim, setBtnAnim] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepsDone, setStepsDone] = useState(0);
  const [typingText, setTypingText] = useState("");
  const [successShow, setSuccessShow] = useState(false);
  const [replayVisible, setReplayVisible] = useState(false);

  const timeoutsRef = useRef([]);
  const intervalsRef = useRef([]);
  const wrapperRef = useRef(null);

  const clearTimers = () => {
    timeoutsRef.current.forEach((id) => clearTimeout(id));
    intervalsRef.current.forEach((id) => clearInterval(id));
    timeoutsRef.current = [];
    intervalsRef.current = [];
  };

  const scheduleTimeout = (fn, delay) => {
    const id = window.setTimeout(fn, delay);
    timeoutsRef.current.push(id);
  };

  const scheduleInterval = (fn, delay) => {
    const id = window.setInterval(fn, delay);
    intervalsRef.current.push(id);
    return id;
  };

  useEffect(() => {
    if (sceneIndex === 1) {
      setProgress(0);
      setStepsDone(0);
      scheduleTimeout(() => setProgress(100), 120);

      [1, 2, 3, 4].forEach((_, idx) => {
        scheduleTimeout(() => setStepsDone(idx + 1), 600 * (idx + 1));
      });
    }

    if (sceneIndex === 2) {
      const textTarget =
        "I  noticed this role requires currently pursuing or recently completed a Bachelor's/Master's degree. Could you share more about your relevant experience in this area?";
      setTypingText("");
      let i = 0;
      const intervalId = scheduleInterval(() => {
        setTypingText((prev) => prev + textTarget.charAt(i));
        i += 1;
        if (i >= textTarget.length) {
          clearInterval(intervalId);
        }
      }, 25);
    }

    if (sceneIndex === 3) {
      setSuccessShow(false);
      scheduleTimeout(() => setSuccessShow(true), 50);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIndex]);

  const runSequence = () => {
    clearTimers();
    setReplayVisible(false);

    // reset scene-specific state
    setSceneIndex(0);
    setFileAnim(false);
    setBtnAnim(false);
    setProgress(0);
    setStepsDone(0);
    setTypingText("");
    setSuccessShow(false);

    scheduleTimeout(() => setFileAnim(true), 50);
    scheduleTimeout(() => setBtnAnim(true), 80);

    // scene transitions
    scheduleTimeout(() => setSceneIndex(1), T1);
    scheduleTimeout(() => setSceneIndex(2), T1 + T2);
    scheduleTimeout(() => setSceneIndex(3), T1 + T2 + T3);

    // show replay button a bit after final scene starts
    scheduleTimeout(() => setReplayVisible(true), T1 + T2 + T3 + 600);
  };

  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          runSequence();
          observer.disconnect(); // Run only once
        }
      },
      { threshold: 0.3 }, // fire when 30% visible
    );

    observer.observe(element);

    return () => {
      clearTimers();
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusText =
    sceneIndex === 0
      ? "Upload CV"
      : sceneIndex === 1
        ? "Skills & Experience Parsed"
        : sceneIndex === 2
          ? "Gaps Clarified with AI"
          : "Application Complete";

  return (
    <Wrapper ref={wrapperRef}>
      <StatusPill>{statusText}</StatusPill>

      <FlowCard>
        <FlowInner>
          {/* Scene 1 */}
          <Scene active={sceneIndex === 0}>
            <S1Header>VASU</S1Header>
            <S1Text>
              Hi! I'm your AI career assistant. I'll help you apply for the
              Product Management Intern position. Let's start by uploading your
              CV so I can understand your background better.
            </S1Text>

            <FileBox animate={fileAnim}>
              <FileIcon>📄</FileIcon>
              <div>
                <FileMain>Resume.pdf</FileMain>
                <FileSub>Click to change file</FileSub>
              </div>
              <FileCheck>✔</FileCheck>
            </FileBox>

            <PrimaryButton animate={btnAnim}>
              Upload &amp; Continue
            </PrimaryButton>
          </Scene>

          {/* Scene 2 */}
          <Scene active={sceneIndex === 1}>
            <S2Title>Analyzing your CV</S2Title>
            <S2Caption>
              VASU is reviewing your background and matching it with the job
              requirements…
            </S2Caption>

            <ProgressTrack>
              <ProgressInner width={progress} />
            </ProgressTrack>

            <Steps>
              <StepItem done={stepsDone >= 1}>
                <span className="check">✓</span>Parsing CV content
              </StepItem>
              <StepItem done={stepsDone >= 2}>
                <span className="check">✓</span>Extracting skills &amp;
                experience
              </StepItem>
              <StepItem done={stepsDone >= 3}>
                <span className="check">✓</span>Matching with job requirements
              </StepItem>
              <StepItem done={stepsDone >= 4}>
                <span className="check">✓</span>Preparing personalized questions
              </StepItem>
            </Steps>
          </Scene>

          {/* Scene 3 */}
          <Scene active={sceneIndex === 2}>
            <AssistantName>VASU</AssistantName>
            <ChatBubble>
              <TypingText>{typingText}</TypingText>
            </ChatBubble>
            <InputArea>Type your answer here…</InputArea>
            <Hint>Press Enter to submit, Shift + Enter for new line</Hint>
          </Scene>

          {/* Scene 4 */}
          <Scene active={sceneIndex === 3}>
            <SuccessWrapper>
              <SuccessIcon show={successShow}>✓</SuccessIcon>
              <SuccessTitle>Application Complete</SuccessTitle>
              <SuccessText>
                VASU has evaluated your skills and submitted your enhanced
                application for review.
              </SuccessText>
              <OutlineButton>View Application</OutlineButton>
            </SuccessWrapper>
          </Scene>
        </FlowInner>
      </FlowCard>

      <ReplayButton visible={replayVisible} onClick={runSequence}>
        <span>↺</span> Replay
      </ReplayButton>
    </Wrapper>
  );
}

export default ApplicantFlow;
